import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Observer } from 'rxjs/Observer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/throw';
import { Http, Response, RequestOptionsArgs, Headers, URLSearchParams } from '@angular/http';
import { AuthHttp } from 'angular2-jwt';
import { NotificationsService } from 'angular2-notifications';

import { environment } from '../../environments/environment';

class BaseApi {
  constructor(protected baseUrl: string,
              protected backendUrl: string,
              protected http: Http | AuthHttp,
              protected notification: NotificationsService) {
  }

  post(url: string, body: any, options?: RequestOptionsArgs, donTBuildBody?: boolean): Observable<any> {
    let headers: RequestOptionsArgs = this.postHeaders();
    if (options === null) {
      headers = null
    }
    if (!donTBuildBody) {
      body = this.buildBody(body)
    }
    return this.http.post(this.buildUrl(url), body, headers)
      .catch(this.handleError.bind(this))
      .map(this.extractData);
  }

  put(url: string, body: any, options?: RequestOptionsArgs, donTBuildBody?: boolean): Observable<any> {
    let headers: RequestOptionsArgs = this.postHeaders();
    if (options === null) {
      headers = null
    }
    if (!donTBuildBody) {
      body = this.buildBody(body)
    }
    return this.http.put(this.buildUrl(url), body, headers)
      .catch(this.handleError.bind(this))
      .map(this.extractData);
  }

  delete(url: string, options?: RequestOptionsArgs): Observable<Response> {
    return this.http.delete(this.buildUrl(url))
      .catch(this.handleError.bind(this))
      .map((res: Response) => res);
  }

  // only used by getCountries, because we need to get a local file
  // instead of making a call to apiv4
  localGet(url: string): Observable<any> {
    return this.http.get(url).map(this.extractData);
  }

  // return the raw response; in case you need headers etc.
  rawResponseGet(url: string): Observable<any> {
    let options: RequestOptionsArgs = {};
    options.headers = this.getHeaders();
    return this.http.get(this.buildUrl(url), options)
      .catch(this.handleError.bind(this))
  } 

  // /!\ it only returns the body, headers etc. are discarded
  get(url: string, options?: RequestOptionsArgs): Observable<any> {
    if (!options) {
      options = {};
    }

    if (!options.headers) {
      options.headers = this.getHeaders();
    }

    return this.http.get(this.buildUrl(url), options)
      .catch(this.handleError.bind(this))
      .map(this.extractData);
  }

  extractData(res: Response): string {
    if (res.status < 200 || res.status >= 300) {
      throw new Error('Bad response status: ' + res.status);
    }

    if (res.text() !== '') {
      let contentType = res.headers.get('Content-Type');
      if (!contentType) {
        contentType = res.headers.get('content-type');
      }

      if (contentType.indexOf('application/json') !== -1) {
        return res.json() || {};
      } else {
        return res.text();
      }
    } else {
      return null;
    }
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  getBackendUrl(): string {
    return this.backendUrl;
  }

  buildUrl(url: any): string {
    return this.getBaseUrl() + '/' + url.replace(/^\//, '');
  }

  buildBody(body: any) {
    if (typeof body !== 'string') {
      return JSON.stringify(body);
    }

    return body;
  }

  handleError(err: Response): Observable<Response> {
    // console.log(err);
    const body = err.json();
    // First condition : API is down
    // Second condition : API internal error code
    console.log(err, body, !!body.code, !!body.status_code);
    if ((!!body.type && body.loaded === 0) || (!!body.code && body.code === 'INTERNAL_ERROR')) {
      this.notification.error('ERROR', 'An unrecoverable error occured. Contact the administrator.');
    } else if (!!body.status_code && body.status_code === 401) {
      // Wrong credentials
      this.notification.error('ERROR', body.description);
    } else if (!!body.code || (!body.code && body.message)) {
      // Default API error format
      this.notification.error('ERROR', JSON.stringify(body.message));
    } else if (err.status === 404) {
      this.notification.error('ERROR', 'Entity not found');
    } else if (err.status === 403) {
      this.notification.error('ERROR', 'You don\'t have the permission to access the requested resource.');
    }
    return Observable.throw(err);
  }

  buildUrlSearchParams(params: any = {}): URLSearchParams {
    const searchParams = new URLSearchParams();
    _.forOwn(params, function (value, key) {
      searchParams.set(key, value);
    });
    return searchParams;
  }

  postHeaders(): RequestOptionsArgs {
    return {
      'headers': new Headers(
        {
           'Content-Type': 'application/json',
           'Accept': 'application/json',
           'X-App-Type': 'backoffice',
           'X-App-Version': environment.version,
           'X-App-User-Id': localStorage.getItem('userid'),
        }
      )
    };
  }

  getHeaders(): Headers {
    return new Headers(
      {
        'Accept': 'application/json',
        'X-App-Type': 'backoffice',
        'X-App-Version': environment.version,
        'X-App-User-Id': localStorage.getItem('userid'),
      }
    );
  }

  makeFileRequest(url: string, file: File): Observable<any> {
    if (!file) {
      return Observable.throw('No file selected');
    }

    return Observable.create((observer: Observer<{ string: number }>) => {
      const formData: FormData = new FormData();
      const xhr: XMLHttpRequest = new XMLHttpRequest();

      formData.append('file', file, file.name);

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            observer.next(JSON.parse(xhr.response));
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };

      xhr.open('POST', this.buildUrl(url), true);
      xhr.setRequestHeader('X-App-Type', 'backoffice');
      xhr.setRequestHeader('X-App-Version', environment.version);
      xhr.setRequestHeader('X-App-User-Id', localStorage.getItem('userid'));
      xhr.setRequestHeader('Authorization', 'JWT ' + localStorage.getItem('token'));
      xhr.send(formData);
    });
  }

  /**
   * Call the urlPath api endpoint
   * and use XMLHttpRequest to get a arraybuffer useful to create ObjectURL link
   * from binary datas
   * @param {string} urlPath - The url to call
   * @param {string} contentType - The type of the download file to generate
   */
  downloadFile(urlPath: string, contentType: string): Observable<string> {
    return Observable.create((observer: Observer<string>) => {
      const req = new XMLHttpRequest();
      const url = environment.baseUrl + urlPath;
      req.open('get', url);
      req.responseType = 'arraybuffer';
      req.setRequestHeader('Accept', 'application/json');

      req.setRequestHeader('X-App-Type', 'backoffice');
      req.setRequestHeader('X-App-Version', environment.version);
      req.setRequestHeader('X-App-User-Id', localStorage.getItem('userid'));
      req.setRequestHeader('Authorization', 'JWT ' + localStorage.getItem('token'));
      req.onreadystatechange = function () { // Call a function when the state changes.
        if (req.readyState === 4 && req.status === 200) {
          const blob = new Blob([req.response], {type: contentType});
          observer.next(URL.createObjectURL(blob));
          observer.complete();
        } else if (req.readyState === 4 && req.status !== 200) {
          const resbody = String.fromCharCode.apply(null, new Uint8Array(req.response)) + '';
          observer.error({json: () => JSON.parse(resbody)});
        }
      };
      req.send(null);
    })
      .catch(this.handleError.bind(this));
  }
}

@Injectable()
export class Api extends BaseApi {
  constructor(protected authHttp: AuthHttp, protected notifications: NotificationsService) {
    super(environment.baseUrl, environment.backendUrl, authHttp, notifications);
  }
}

@Injectable()
export class NoAuthApi extends BaseApi {
  constructor(protected http: Http, protected notifications: NotificationsService) {
    super(environment.baseUrl, environment.backendUrl, http, notifications);
  }
}



// WEBPACK FOOTER //
// /app/src/app/services/api.service.ts